import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchCategories();
    // Lire le paramètre category de l'URL
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      const allCategories = response.data;
      
      // Organiser en hiérarchie : parents avec leurs enfants
      const parents = allCategories.filter(cat => !cat.parent_id);
      const categoriesWithSubs = parents.map(parent => ({
        ...parent,
        subcategories: allCategories.filter(cat => cat.parent_id === parent.id)
      }));
      
      setCategories(categoriesWithSubs);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const url = selectedCategory
        ? `${API}/products?light=true&category=${selectedCategory}`
        : `${API}/products?light=true`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-8" data-testid="products-page-title">
          Nos Produits
        </h1>

        {/* Filters */}
        <div className="mb-8 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-lg pl-12 pr-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 transition"
              data-testid="search-input"
            />
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                selectedCategory === ''
                  ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white neon-cyan'
                  : 'bg-white text-gray-700 hover:text-cyan-600 border border-gray-300'
              }`}
              data-testid="category-all"
            >
              Tous
            </button>
            {categories.map(category => (
              category.subcategories && category.subcategories.length > 0 ? (
                <div key={category.id} className="relative group">
                  <button
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-1 ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white neon-cyan'
                        : 'bg-white text-gray-700 hover:text-cyan-600 border border-gray-300'
                    }`}
                  >
                    {category.name}
                    <span className="text-xs">▾</span>
                  </button>
                  
                  {/* Dropdown sous-catégories */}
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 min-w-[200px]">
                    <div className="py-2">
                      <button
                        onClick={() => setSelectedCategory(category.id)}
                        className="block w-full text-left px-4 py-2 text-sm font-medium text-cyan-600 hover:bg-cyan-50 transition"
                      >
                        → Tout {category.name}
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                      {category.subcategories.map(subcat => (
                        <button
                          key={subcat.id}
                          onClick={() => setSelectedCategory(subcat.id)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-600 transition"
                        >
                          {subcat.icon && <span className="mr-2">{subcat.icon}</span>}
                          {subcat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white neon-cyan'
                      : 'bg-white text-gray-700 hover:text-cyan-600 border border-gray-300'
                  }`}
                  data-testid={`category-${category.slug}`}
                >
                  {category.name}
                </button>
              )
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Aucun produit actuellement</h3>
            <p className="text-gray-500 mb-4">
              {selectedCategory 
                ? "Cette catégorie sera bientôt remplie avec de nouveaux produits."
                : searchQuery 
                  ? "Aucun produit ne correspond à votre recherche."
                  : "Aucun produit disponible pour le moment."}
            </p>
            <button
              onClick={() => {
                setSelectedCategory('');
                setSearchQuery('');
              }}
              className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white px-6 py-2 rounded-lg hover:neon-cyan transition"
            >
              Voir tous les produits
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;